import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography, keyframes } from "@mui/material";
import WifiOffIcon from "@mui/icons-material/WifiOff";

// Same UX as the portal's ServerDownView (kept separate — portal must stay self-contained)
// Retry schedule: every 10s during the first 50s, then every 5s, up to MAX_ATTEMPTS in total.
const SLOW_INTERVAL_SECONDS = 10;
const FAST_INTERVAL_SECONDS = 5;
const SLOW_PHASE_SECONDS = 50;
const MAX_ATTEMPTS = 50;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.4; transform: scale(0.92); }
`;

interface ServerDownViewProps {
  /** Called on every attempt. The parent unmounts this view once the server is back. */
  onRetry: () => Promise<void> | void;
}

export function ServerDownView({ onRetry }: ServerDownViewProps) {
  const startedAtRef = useRef(Date.now());
  const onRetryRef = useRef(onRetry);
  onRetryRef.current = onRetry;

  const nextInterval = () =>
    Date.now() - startedAtRef.current < SLOW_PHASE_SECONDS * 1000 ? SLOW_INTERVAL_SECONDS : FAST_INTERVAL_SECONDS;

  const [interval, setIntervalSeconds] = useState(SLOW_INTERVAL_SECONDS);
  const [countdown, setCountdown] = useState(SLOW_INTERVAL_SECONDS);
  const [attempts, setAttempts] = useState(0);
  const [checking, setChecking] = useState(false);
  const gaveUp = attempts >= MAX_ATTEMPTS;

  const retry = useCallback(async () => {
    setChecking(true);
    setAttempts((a) => a + 1);
    try {
      await onRetryRef.current();
    } finally {
      const next = nextInterval();
      setChecking(false);
      setIntervalSeconds(next);
      setCountdown(next);
    }
  }, []);

  const restart = () => {
    startedAtRef.current = Date.now();
    setAttempts(0);
    retry();
  };

  useEffect(() => {
    if (checking || gaveUp) return;
    if (countdown <= 0) {
      retry();
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, checking, gaveUp, retry]);

  if (gaveUp && !checking) {
    return (
      <Box dir="rtl" sx={{ textAlign: "center", mt: 3 }}>
        <WifiOffIcon sx={{ fontSize: 48, color: "error.main", mb: 1 }} />
        <Typography variant="body1" fontWeight={700} gutterBottom>
          השרת עדיין לא זמין
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          הפסקנו לנסות אוטומטית אחרי {MAX_ATTEMPTS} ניסיונות. אם הבעיה נמשכת יש לפנות לתמיכה.
        </Typography>
        <Button variant="outlined" color="warning" size="small" onClick={restart}>
          נסה שוב
        </Button>
      </Box>
    );
  }

  return (
    <Box dir="rtl" sx={{ textAlign: "center", mt: 3 }}>
      <Box sx={{ animation: `${pulse} 2s ease-in-out infinite`, display: "inline-flex", mb: 3 }}>
        <WifiOffIcon sx={{ fontSize: 72, color: "warning.main" }} />
      </Box>

      <Typography variant="h5" fontWeight={700} gutterBottom>
        מתחברים לשרת...
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={1}>
        השרת זמנית אינו זמין
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={4}>
        זה בדרך כלל מסתדר תוך דקה-שתיים. אנו מנסים להתחבר מחדש אוטומטית.
      </Typography>

      <Box sx={{ position: "relative", display: "inline-flex", mb: 2 }}>
        <CircularProgress
          variant={checking ? "indeterminate" : "determinate"}
          value={(countdown / interval) * 100}
          size={80}
          thickness={3}
          sx={{ color: "warning.main" }}
        />
        <Box
          sx={{
            top: 0, left: 0, bottom: 0, right: 0,
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!checking && (
            <Typography variant="h6" fontWeight={700} color="text.secondary">
              {countdown}
            </Typography>
          )}
        </Box>
      </Box>

      <Typography variant="caption" color="text.disabled" sx={{ display: "block" }}>
        {checking ? "בודק חיבור..." : `בדיקה הבאה בעוד ${countdown} שניות`}
      </Typography>
    </Box>
  );
}
